import Phaser from 'phaser';
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  GROUND_Y,
  LAUNCH_X,
  LAUNCH_Y,
  MAX_POWER,
  MIN_POWER,
  WORLD_WIDTH,
} from '../config/gameConstants';
import { AnimationKeys, ChaosFrame, SceneKeys, TextureKeys } from '../config/keys';
import { ChaosSoundManager } from '../managers/ChaosSoundManager';
import { DistanceManager } from '../managers/DistanceManager';
import { CourseProp } from '../types/gameState';

interface ActiveProp {
  sprite: Phaser.Physics.Arcade.Image;
  data: CourseProp;
  used: boolean;
}

type GamePhase = 'aiming' | 'launched' | 'settled' | 'paused';

const COURSE: CourseProp[] = [
  { label: 'acorn bounce', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Whoopee, x: 620, y: 410, scale: 0.36, effect: 'boost', bounceX: 380, bounceY: -650, spin: 300, gag: 'giant acorn demanded liftoff' },
  { label: 'leaf ramp', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Banana, x: 980, y: 408, scale: 0.34, effect: 'boost', bounceX: 500, bounceY: -360, spin: 520, gag: 'leaf ramp did squirrel math' },
  { label: 'nut soda spill', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Soda, x: 1320, y: 430, scale: 0.28, effect: 'slow', bounceX: 120, bounceY: -210, spin: -120, gag: 'nut soda puddle got clingy' },
  { label: 'leaf blower', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Fan, x: 1700, y: 392, scale: 0.34, effect: 'boost', bounceX: 580, bounceY: -300, spin: 360, gag: 'tiny leaf blower became weather' },
  { label: 'mystery lunchbox', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Lunchbox, x: 2140, y: 384, scale: 0.34, effect: 'slow', bounceX: 80, bounceY: -320, spin: 80, gag: 'mystery lunchbox reduced morale' },
  { label: 'hollow log', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Couch, x: 2620, y: 400, scale: 0.4, effect: 'boost', bounceX: 460, bounceY: -580, spin: -260, gag: 'springy log entered orbit' },
  { label: 'nut soda splash', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Soda, x: 3100, y: 422, scale: 0.27, effect: 'slow', bounceX: 60, bounceY: -260, spin: 220, gag: 'nut soda said stay a while' },
  { label: 'springy log', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Couch, x: 3600, y: 394, scale: 0.36, effect: 'special', bounceX: 640, bounceY: -560, spin: 420, gag: 'springy log approved the launch' },
  { label: 'gnome', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Duck, x: 4180, y: 410, scale: 0.34, effect: 'boost', bounceX: 520, bounceY: -460, spin: -340, gag: 'garden gnome said absolutely not' },
  { label: 'hollow log drag', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Couch, x: 4700, y: 402, scale: 0.32, effect: 'slow', bounceX: 150, bounceY: -270, spin: 180, gag: 'hollow log requested nap time' },
  { label: 'nut soda', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Soda, x: 5260, y: 398, scale: 0.34, effect: 'boost', bounceX: 430, bounceY: -720, spin: 280, gag: 'nut-butter soda discovered propulsion' },
  { label: 'leaf blower gust', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Fan, x: 5880, y: 412, scale: 0.34, effect: 'slow', bounceX: 180, bounceY: -420, spin: 600, gag: 'leaf blower gust was full of acorns' },
  { label: 'picnic basket', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Lunchbox, x: 6500, y: 390, scale: 0.32, effect: 'special', bounceX: 560, bounceY: -620, spin: -520, gag: 'mystery picnic basket chose chaos' },
  { label: 'picnic basket flip', texture: TextureKeys.ObstacleSheet, frame: ChaosFrame.Lunchbox, x: 7200, y: 396, scale: 0.34, effect: 'special', bounceX: 390, bounceY: -500, spin: 760, gag: 'picnic basket flipped the narrative' },
];

const COURSE_LOOP_WIDTH = 7200;
const COURSE_AHEAD_BUFFER = 4200;
const CLEANUP_DISTANCE = 1800;

export class GameScene extends Phaser.Scene {
  private squirrel!: Phaser.Physics.Arcade.Sprite;
  private cannon!: Phaser.GameObjects.Image;
  private props!: Phaser.Physics.Arcade.StaticGroup;
  private activeProps: ActiveProp[] = [];
  private distanceManager = new DistanceManager();
  private soundManager = new ChaosSoundManager();
  private phase: GamePhase = 'aiming';
  private phaseBeforePause: Exclude<GamePhase, 'paused'> = 'aiming';
  private aimGraphics!: Phaser.GameObjects.Graphics;
  private powerMeterGraphics!: Phaser.GameObjects.Graphics;
  private hudText!: Phaser.GameObjects.Text;
  private powerText!: Phaser.GameObjects.Text;
  private muteText!: Phaser.GameObjects.Text;
  private funniestCollision = 'the squirrel cleared the bird feeder';
  private aimAngle = -34;
  private aimPower = 700;
  private powerDirection = 1;
  private launchReadyAt = 0;
  private settleTimer = 0;
  private worldEnd = WORLD_WIDTH;
  private nextCourseOffset = COURSE_LOOP_WIDTH;
  private backgroundBuiltTo = 0;
  private groundBuiltTo = 0;

  constructor() {
    super(SceneKeys.Game);
  }

  create(): void {
    this.phase = 'aiming';
    this.activeProps = [];
    this.funniestCollision = 'the squirrel cleared the bird feeder';
    this.settleTimer = 0;
    this.aimPower = MIN_POWER;
    this.powerDirection = 1;
    this.launchReadyAt = this.time.now + 300;
    this.worldEnd = WORLD_WIDTH;
    this.nextCourseOffset = COURSE_LOOP_WIDTH;
    this.backgroundBuiltTo = 0;
    this.groundBuiltTo = 0;

    this.physics.world.setBounds(0, 0, this.worldEnd, GAME_HEIGHT + 160);
    this.cameras.main.setBounds(0, 0, this.worldEnd, GAME_HEIGHT);

    this.extendWorldArt(this.worldEnd);
    this.createCourseProps(0);

    this.cannon = this.add.image(LAUNCH_X - 48, LAUNCH_Y + 20, TextureKeys.ChaosSheet, ChaosFrame.Cannon).setScale(0.42).setDepth(5);
    this.squirrel = this.physics.add
      .sprite(LAUNCH_X, LAUNCH_Y, TextureKeys.ChaosSheet, ChaosFrame.SquirrelHappy)
      .setScale(0.26)
      .setDepth(8)
      .setCollideWorldBounds(false);
    this.squirrel.body?.setSize(210, 150, true);
    this.squirrel.body?.setOffset(76, 98);
    this.squirrel.setDragX(18);
    this.squirrel.setBounce(0.32, 0.46);

    this.aimGraphics = this.add.graphics().setDepth(12);
    this.powerMeterGraphics = this.add.graphics().setDepth(21).setScrollFactor(0);
    this.hudText = this.add
      .text(20, 18, '', this.getHudStyle(22))
      .setDepth(20)
      .setScrollFactor(0);
    this.powerText = this.add
      .text(20, GAME_HEIGHT - 50, '', this.getHudStyle(18))
      .setDepth(20)
      .setScrollFactor(0);
    this.muteText = this.add
      .text(GAME_WIDTH - 20, 18, this.soundManager.isMuted() ? 'sound off' : 'sound on', this.getHudStyle(16))
      .setOrigin(1, 0)
      .setDepth(20)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    const pauseText = this.add
      .text(GAME_WIDTH - 20, 58, 'pause', this.getHudStyle(16))
      .setOrigin(1, 0)
      .setDepth(20)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    this.muteText.on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.muteText.setText(this.soundManager.toggleMuted() ? 'sound off' : 'sound on');
    });
    pauseText.on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.togglePause();
    });

    this.physics.add.overlap(this.squirrel, this.props, (_squirrel, prop) => this.hitProp(prop as Phaser.Physics.Arcade.Image));

    this.bindInput();
    this.updateAimFromAnglePower();
    this.drawAim();
    this.drawPowerMeter();
    this.updateHud();
  }

  update(time: number, delta: number): void {
    if (this.phase === 'paused') {
      return;
    }

    if (this.phase === 'aiming') {
      this.updatePowerMeter(delta);
      this.drawAim();
      this.drawPowerMeter();
    } else if (this.phase === 'launched') {
      this.ensureCourseAhead();
      this.squirrel.setAngle(Phaser.Math.Clamp(this.squirrel.body!.velocity.y / 12, -34, 85));
      this.updateFlightFrame(time);
      this.updateHud();
      this.cleanupOldProps();

      if (this.squirrel.x > 360) {
        this.cameras.main.startFollow(this.squirrel, true, 0.08, 0.08, -260, 72);
      }

      if (this.squirrel.y > GROUND_Y) {
        this.squirrel.y = GROUND_Y;
        if (this.squirrel.body!.velocity.y > 0) {
          this.squirrel.setVelocityY(-Math.abs(this.squirrel.body!.velocity.y) * 0.38);
          this.squirrel.setVelocityX(this.squirrel.body!.velocity.x * 0.84);
          this.spawnImpact(this.squirrel.x, GROUND_Y - 28);
        }
      }

      const slowEnough = Math.abs(this.squirrel.body!.velocity.x) < 34 && Math.abs(this.squirrel.body!.velocity.y) < 46;
      const fellBelowWorld = this.squirrel.y > GAME_HEIGHT + 90;
      if ((slowEnough && this.squirrel.y >= GROUND_Y - 2) || fellBelowWorld) {
        this.settleTimer += delta;
        if (this.settleTimer > 620) {
          this.endGame();
        }
      } else {
        this.settleTimer = 0;
      }
    }
  }

  private bindInput(): void {
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (this.phase !== 'aiming' || this.time.now < this.launchReadyAt) {
        return;
      }
      this.updateAimFromPointer(pointer);
      this.launchSquirrel();
    });

    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (this.phase !== 'aiming') {
        return;
      }
      this.updateAimFromPointer(pointer);
      this.drawAim();
      this.drawPowerMeter();
    });

    this.input.keyboard?.on('keydown-SPACE', () => {
      if (this.phase === 'aiming' && this.time.now >= this.launchReadyAt) {
        this.launchSquirrel();
      }
    });
    this.input.keyboard?.on('keydown-ENTER', () => {
      if (this.phase === 'aiming' && this.time.now >= this.launchReadyAt) {
        this.launchSquirrel();
      }
    });
    this.input.keyboard?.on('keydown-R', () => this.scene.restart());
    this.input.keyboard?.on('keydown-ESC', () => this.togglePause());
    this.input.keyboard?.on('keydown-UP', () => {
      if (this.phase === 'aiming') {
        this.aimAngle = Phaser.Math.Clamp(this.aimAngle - 3, -70, -12);
        this.updateAimFromAnglePower();
        this.drawAim();
      }
    });
    this.input.keyboard?.on('keydown-DOWN', () => {
      if (this.phase === 'aiming') {
        this.aimAngle = Phaser.Math.Clamp(this.aimAngle + 3, -70, -12);
        this.updateAimFromAnglePower();
        this.drawAim();
      }
    });
  }

  private updateAimFromPointer(pointer: Phaser.Input.Pointer): void {
    const angle = Phaser.Math.RadToDeg(Math.atan2(pointer.worldY - LAUNCH_Y, pointer.worldX - LAUNCH_X));
    this.aimAngle = Phaser.Math.Clamp(angle, -70, -10);
    this.updateAimFromAnglePower();
  }

  private updateAimFromAnglePower(): void {
    this.cannon?.setAngle(this.aimAngle + 8);
    this.powerText?.setText(`move mouse to aim  |  click to launch  |  power ${this.getPowerPercent()}%  angle ${Math.abs(Math.round(this.aimAngle))} deg`);
  }

  private updatePowerMeter(delta: number): void {
    this.aimPower += this.powerDirection * delta * 0.72;

    if (this.aimPower >= MAX_POWER) {
      this.aimPower = MAX_POWER;
      this.powerDirection = -1;
    } else if (this.aimPower <= MIN_POWER) {
      this.aimPower = MIN_POWER;
      this.powerDirection = 1;
    }

    this.updateAimFromAnglePower();
  }

  private drawAim(): void {
    this.aimGraphics.clear();
    this.aimGraphics.lineStyle(4, 0x28221a, 0.72);
    this.aimGraphics.fillStyle(0xff6c62, 1);

    const radians = Phaser.Math.DegToRad(this.aimAngle);
    const velocity = new Phaser.Math.Vector2(Math.cos(radians) * this.aimPower, Math.sin(radians) * this.aimPower);
    let previous = new Phaser.Math.Vector2(LAUNCH_X, LAUNCH_Y);

    for (let i = 1; i <= 12; i += 1) {
      const t = i * 0.13;
      const point = new Phaser.Math.Vector2(
        LAUNCH_X + velocity.x * t,
        LAUNCH_Y + velocity.y * t + 0.5 * 780 * t * t,
      );
      this.aimGraphics.lineBetween(previous.x, previous.y, point.x, point.y);
      if (i % 2 === 0) {
        this.aimGraphics.fillCircle(point.x, point.y, 4);
      }
      previous = point;
    }
  }

  private launchSquirrel(): void {
    this.phase = 'launched';
    this.aimGraphics.clear();
    this.powerMeterGraphics.clear();
    this.squirrel.play(AnimationKeys.SquirrelFly);

    const radians = Phaser.Math.DegToRad(this.aimAngle);
    this.squirrel.setVelocity(Math.cos(radians) * this.aimPower, Math.sin(radians) * this.aimPower);
    this.tweens.add({
      targets: this.cannon,
      x: this.cannon.x - 10,
      duration: 80,
      yoyo: true,
      ease: 'Sine.out',
    });
  }

  private createGroundDoodles(startX: number, endX: number): void {
    const ground = this.add.graphics();
    ground.fillStyle(0xa8d86e, 0.22);
    ground.fillRect(startX, GROUND_Y + 18, endX - startX, GAME_HEIGHT - GROUND_Y);
    ground.lineStyle(4, 0x28221a, 0.62);
    ground.beginPath();
    ground.moveTo(startX, GROUND_Y + 16);
    for (let x = startX; x <= endX; x += 48) {
      ground.lineTo(x, GROUND_Y + 16 + Phaser.Math.Between(-5, 4));
    }
    ground.strokePath();

    ground.lineStyle(2, 0x4f8f57, 0.44);
    for (let x = startX + 220; x < endX; x += 210) {
      ground.lineBetween(x, GROUND_Y + 48, x + 22, GROUND_Y + 36);
      ground.lineBetween(x + 26, GROUND_Y + 52, x + 42, GROUND_Y + 40);
    }
  }

  private createBackgroundStrip(untilX: number): void {
    const texture = this.textures.get(TextureKeys.Background).getSourceImage() as HTMLImageElement;
    const segmentWidth = Math.round((GAME_HEIGHT / texture.height) * texture.width);

    for (let x = this.backgroundBuiltTo; x < untilX + segmentWidth; x += segmentWidth) {
      this.add.image(x, 0, TextureKeys.Background).setOrigin(0).setDisplaySize(segmentWidth, GAME_HEIGHT).setDepth(0);
      this.backgroundBuiltTo = x + segmentWidth;
    }
  }

  private extendWorldArt(untilX: number): void {
    this.createBackgroundStrip(untilX);

    const chunkSize = 1600;
    while (this.groundBuiltTo < untilX) {
      const startX = this.groundBuiltTo;
      const endX = Math.min(this.groundBuiltTo + chunkSize, untilX);
      this.createGroundDoodles(startX, endX);
      this.groundBuiltTo = endX;
    }
  }

  private createCourseProps(offsetX: number): void {
    if (!this.props) {
      this.props = this.physics.add.staticGroup();
    }
    COURSE.forEach((propData) => {
      const sprite = this.props
        .create(propData.x + offsetX, propData.y, propData.texture, propData.frame)
        .setScale(propData.scale)
        .setDepth(6) as Phaser.Physics.Arcade.Image;
      sprite.refreshBody();
      const body = sprite.body as Phaser.Physics.Arcade.StaticBody;
      body.setSize(sprite.displayWidth * 0.66, sprite.displayHeight * 0.58);
      body.updateFromGameObject();
      this.activeProps.push({ sprite, data: propData, used: false });
    });
  }

  private ensureCourseAhead(): void {
    if (this.squirrel.x + COURSE_AHEAD_BUFFER < this.worldEnd) {
      return;
    }

    this.worldEnd += COURSE_LOOP_WIDTH;
    this.physics.world.setBounds(0, 0, this.worldEnd, GAME_HEIGHT + 160);
    this.cameras.main.setBounds(0, 0, this.worldEnd, GAME_HEIGHT);
    this.extendWorldArt(this.worldEnd);

    while (this.nextCourseOffset < this.worldEnd) {
      this.createCourseProps(this.nextCourseOffset);
      this.nextCourseOffset += COURSE_LOOP_WIDTH;
    }
  }

  private cleanupOldProps(): void {
    this.activeProps = this.activeProps.filter((item) => {
      if (item.sprite.x < this.squirrel.x - CLEANUP_DISTANCE) {
        item.sprite.destroy();
        return false;
      }

      return true;
    });
  }

  private hitProp(prop: Phaser.Physics.Arcade.Image): void {
    const active = this.activeProps.find((item) => item.sprite === prop);
    if (!active || active.used || this.phase !== 'launched') {
      return;
    }

    active.used = true;
    active.sprite.setAlpha(0.52);
    this.funniestCollision = active.data.gag;

    const currentX = this.squirrel.body!.velocity.x;
    const launchX = active.data.effect === 'slow' ? Math.max(currentX * 0.44, active.data.bounceX) : Math.max(currentX + active.data.bounceX, active.data.bounceX);
    this.squirrel.setVelocity(launchX, active.data.bounceY);
    this.squirrel.setAngularVelocity(active.data.spin);
    this.spawnImpact(active.sprite.x, active.sprite.y - 44);

    this.tweens.add({
      targets: active.sprite,
      y: active.sprite.y - 18,
      angle: active.data.spin > 0 ? 8 : -8,
      duration: 90,
      yoyo: true,
      ease: 'Sine.out',
    });
  }

  private spawnImpact(x: number, y: number): void {
    const burst = this.add.image(x, y, TextureKeys.ObstacleSheet, ChaosFrame.Whoopee).setScale(0.2).setDepth(10).setAlpha(0.9);
    this.tweens.add({
      targets: burst,
      scale: 0.4,
      alpha: 0,
      angle: 14,
      duration: 360,
      ease: 'Sine.out',
      onComplete: () => burst.destroy(),
    });
  }

  private updateFlightFrame(time: number): void {
    if (this.squirrel.body!.velocity.y < -240) {
      this.squirrel.setFrame(ChaosFrame.SquirrelTriumph);
      return;
    }
    if (time % 280 < 140) {
      this.squirrel.setFrame(ChaosFrame.SquirrelHappy);
    } else {
      this.squirrel.setFrame(ChaosFrame.SquirrelTumble);
    }
  }

  private updateHud(): void {
    const distance = this.distanceManager.getDistance(this.squirrel.x);
    this.hudText.setText(`distance ${distance}   best ${this.distanceManager.getBestDistance()}`);
  }

  private drawPowerMeter(): void {
    const x = 20;
    const y = GAME_HEIGHT - 96;
    const width = 310;
    const height = 26;
    const fillWidth = Phaser.Math.Linear(0, width, (this.aimPower - MIN_POWER) / (MAX_POWER - MIN_POWER));

    this.powerMeterGraphics.clear();
    this.powerMeterGraphics.fillStyle(0xfff7d8, 0.94);
    this.powerMeterGraphics.lineStyle(4, 0x28221a, 0.82);
    this.powerMeterGraphics.fillRoundedRect(x, y, width + 82, height + 34, 8);
    this.powerMeterGraphics.strokeRoundedRect(x, y, width + 82, height + 34, 8);
    this.powerMeterGraphics.fillStyle(0x28221a, 1);
    this.powerMeterGraphics.fillRect(x + 74, y + 22, width, height);
    this.powerMeterGraphics.fillGradientStyle(0x62e4ef, 0xf8d36d, 0x62e4ef, 0xff6c62, 1);
    this.powerMeterGraphics.fillRect(x + 74, y + 22, fillWidth, height);
    this.powerMeterGraphics.lineStyle(2, 0xfff7d8, 0.75);
    for (let i = 1; i < 10; i += 1) {
      const tickX = x + 74 + i * (width / 10);
      this.powerMeterGraphics.lineBetween(tickX, y + 22, tickX, y + 48);
    }
    this.powerMeterGraphics.lineStyle(4, 0x28221a, 0.92);
    this.powerMeterGraphics.strokeRect(x + 74, y + 22, width, height);
    this.powerMeterGraphics.fillStyle(0x28221a, 1);
    this.powerMeterGraphics.fillTriangle(x + 74 + fillWidth, y + 56, x + 66 + fillWidth, y + 68, x + 82 + fillWidth, y + 68);
    this.powerMeterGraphics.fillStyle(0x28221a, 1);
    this.powerMeterGraphics.fillRoundedRect(x + 16, y + 16, 44, 38, 6);
  }

  private getPowerPercent(): number {
    return Math.round(((this.aimPower - MIN_POWER) / (MAX_POWER - MIN_POWER)) * 100);
  }

  private togglePause(): void {
    if (this.phase === 'settled') {
      return;
    }
    if (this.phase === 'paused') {
      this.phase = this.phaseBeforePause;
      this.physics.resume();
      this.children.getByName('pause-label')?.destroy();
      return;
    }
    this.phaseBeforePause = this.phase;
    this.phase = 'paused';
    this.physics.pause();
    this.add
      .text(this.cameras.main.scrollX + GAME_WIDTH / 2, GAME_HEIGHT / 2, 'paused', this.getHudStyle(42))
      .setOrigin(0.5)
      .setDepth(30)
      .setName('pause-label');
  }

  private endGame(): void {
    this.phase = 'settled';
    this.squirrel.stop();
    this.squirrel.setFrame(ChaosFrame.SquirrelAnnoyed);
    this.squirrel.setAngularVelocity(0);
    const distance = this.distanceManager.getDistance(this.squirrel.x);
    const bestDistance = this.distanceManager.saveBestDistance(distance);

    this.time.delayedCall(520, () => {
      this.scene.start(SceneKeys.GameOver, {
        distance,
        bestDistance,
        funniestCollision: this.funniestCollision,
      });
    });
  }

  private getHudStyle(fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
      fontSize: `${fontSize}px`,
      color: '#28221a',
      backgroundColor: '#fff7d8',
      padding: { x: 10, y: 6 },
    };
  }
}
